import React, { createContext, useContext, useEffect, useState, type ReactNode, useCallback, useMemo } from 'react';
import { apiService } from '../services/api';
import { handleOIDCRedirect } from '../utils/oidc';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  userOid: string | null;
  userName: string | null;
  authError: string | null;
  logout: () => Promise<void>;
  clearError: () => void;
  user: any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [userOid, setUserOid] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Check authentication from JWT token
  const isAuthenticated = apiService.isAuthenticated();

  // Initialize authentication on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        
        // Check if we're being redirected back from OIDC with a token
        const token = handleOIDCRedirect();
        if (token) {
          // Store the token
          localStorage.setItem('auth_token', token);
          console.log('✅ OIDC authentication successful');
          
          // Fetch and store user info from the token
          try {
            const userInfo = await apiService.getCurrentUser();
            // Use OIDC user ID if available, otherwise fall back to app user ID
            setUserOid(userInfo.oidcUserId || userInfo.id);
            setUserName(userInfo.name);
            setUser(userInfo);
            console.log('✅ User info loaded from token:', userInfo.email);
          } catch (error) {
            console.error('Failed to fetch user info after OIDC login:', error);
            // If we can't fetch user info, clear the token
            localStorage.removeItem('auth_token');
          }
        } else if (apiService.isAuthenticated()) {
          // Check if user is authenticated via JWT token (regular login)
          const storedUser = apiService.getStoredUser();
          if (storedUser) {
            // Use OIDC user ID if available, otherwise fall back to app user ID
            setUserOid(storedUser.oidcUserId || storedUser.id);
            setUserName(storedUser.name);
            setUser(storedUser);
            console.log('✅ User authenticated:', storedUser.email);
          } else {
            // Token exists but no user info - fetch it
            try {
              const userInfo = await apiService.getCurrentUser();
              // Use OIDC user ID if available, otherwise fall back to app user ID
              setUserOid(userInfo.oidcUserId || userInfo.id);
              setUserName(userInfo.name);
              setUser(userInfo);
              console.log('✅ User info loaded from token:', userInfo.email);
            } catch (error) {
              console.error('Failed to fetch user info:', error);
              // Clear invalid token
              localStorage.removeItem('auth_token');
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuthError('Failed to initialize authentication');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      // Clear database JWT
      await apiService.logout();
      
      setUserOid(null);
      setUserName(null);
      setUser(null);
      
      console.log('✅ User logged out');
    } catch (error) {
      console.error('Logout error:', error);
      setAuthError('Logout failed');
    }
  }, []);

  const clearError = useCallback(() => {
    setAuthError(null);
  }, []);

  const value: AuthContextType = useMemo(() => ({
    isAuthenticated,
    isLoading,
    userOid,
    userName,
    authError,
    logout,
    clearError,
    user,
  }), [isAuthenticated, isLoading, userOid, userName, authError, logout, clearError, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
